import React, { useMemo, useState, useCallback, useEffect } from 'react';
import cn from 'classnames';
import Tags from 'components/UI/Tags';
import { Grid, GridItem } from 'components/UI/Grid';
import Card from './Card';
import { useLang, useTrans } from 'utils/localisation';

import * as styles from './InternalEvents.module.scss';

export default function InternalEvents({ cards, eventsPage }) {
  const t = useTrans(['events', 'common']);
  const lang = useLang();
  const preparedLang = lang === 'uk' || lang === 'kz' || lang === 'pl' ? 'en' : lang;
  const targetDate = formatDate(new Date());

  const [limiter, setLimiter] = useState(1);
  const defaultShowCount = eventsPage ? 3 : limiter;
  const [showCount, setShowCount] = useState(defaultShowCount);
  const [active, setActive] = useState('upcoming');
  const [opacity, setOpacity] = useState({
    '--opacity': 1,
  });

  const gridSettings = {
    xsm: { cols: 1, gutterX: '20px', gutterY: '20px' },
    sm: { cols: 2, gutterX: '20px', gutterY: '20px' },
    md: { cols: 2, gutterX: '20px', gutterY: '40px' },
    lg: { cols: 2, gutterX: '20px', gutterY: '40px' },
    xl: { cols: 2, gutterX: '30px', gutterY: '40px' },
  };

  const setActiveTab = type => {
    setOpacity({ '--opacity': 0.1 });
    setTimeout(() => {
      setActive(type);
      setOpacity({ '--opacity': 1 });
    }, 200);
  };

  const showMoreHandle = useCallback(() => {
    setLimiter(limiter === 1 ? 2 : 1);
    setShowCount(prev => prev + limiter);
  }, [limiter]);

  useEffect(() => {
    if (!eventsPage) {
      setLimiter(2);
    }
  }, [eventsPage]);

  const { futureCardList, previousCardList } = useMemo(() => {
    const futureCardList = cards
      ?.filter(({ finishDate }) => finishDate >= targetDate)
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    const previousCardList = cards
      ?.filter(({ finishDate }) => finishDate < targetDate)
      .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

    return { futureCardList, previousCardList };
  }, [cards, targetDate]);

  const displayTags = futureCardList?.length >= 1;

  const tabsContent = useMemo(() => {
    return [
      {
        name: t('upcomingTab'),
        type: futureCardList?.length >= 1 ? 'upcoming' : 'previous',
        cards: futureCardList?.length >= 1 ? futureCardList : [],
      },
      {
        name: t('previousTab'),
        type: futureCardList?.length >= 1 ? 'previous' : 'upcoming',
        cards: previousCardList || [],
      },
    ].filter(tab => tab.cards.length > 0);
  }, [futureCardList, previousCardList]);

  const getHiddenItems = tabCards => tabCards.length - showCount;

  return (
    <div className={cn(styles.wrapper, displayTags && styles.withTags)}>
      {cards ? (
        <>
          {displayTags && (
            <Tags
              className={styles.tags}
              active={active}
              handleClick={setActiveTab}
              tags={tabsContent}
            />
          )}
          <div className={styles.content} style={opacity}>
            {tabsContent.map(({ type, cards }) => (
              <div hidden={active !== type} key={type}>
                <div className={styles.cards}>
                  <Grid {...gridSettings}>
                    {cards?.slice(0, showCount).map((card, i) => {
                      const isBig = checkLgWidth(i, cards.length);
                      const title = card.title[preparedLang];
                      const subtitle = card.subtitle[preparedLang];
                      const location = card.location[preparedLang];
                      const global = card.global?.[preparedLang];
                      return (
                        <GridItem key={card.id} colSpan={isBig ? 2 : undefined}>
                          <Card
                            {...card}
                            title={title}
                            subtitle={subtitle}
                            location={location}
                            global={global}
                            theme="internal"
                            isBig={isBig}
                          />
                        </GridItem>
                      );
                    })}
                  </Grid>
                  {showCount < cards?.length && getHiddenItems(cards) > 0 && (
                    <div className={styles.buttonRow}>
                      <button className={styles.button} onClick={showMoreHandle}>
                        {`${t('seeMore')} (${getHiddenItems(cards)})`}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function checkLgWidth(i, length) {
  // the first card always should be large
  if (i === 0) return true;

  // every third card should be large
  if (i % 3 === 0) return true;

  // if one card left in the end it should be large
  return i === length - 1 && i % 3 === 1 && length >= 4;
}
